import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import { AccommodationPackageOptionTileVisualStyle } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { getCanSurfacePropertyAnytimeRates } from 'selectors/offerPage/offerPageSelectors'

import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import { isPropertyInclusionValidForDuration } from './singlePropertyUtils'

export const AnytimeRateOptionTile = styled.div`
  ${AccommodationPackageOptionTileVisualStyle}

  display: grid;
  gap: ${rem(12)} ${rem(32)};
  // This is for the optional implicit pricing column.
  grid-auto-columns: min-content;
  grid-template:
    "details" min-content
    "inclusions" min-content
    / 1fr;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)} ${rem(16)};
    // This is for the optional implicit pricing column.
    grid-auto-columns: ${rem(285)};
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)} ${rem(32)};
    &:not(.layout-room-card) {
      grid-template:
        "details inclusions" min-content
        / ${rem(238)} 1fr;
    }
  }

  &.layout-room-card {
    border: 0;
    border-top: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};

    @container (min-width: ${rem(640)}) {
      grid-template:
        "details inclusions" min-content
        / ${rem(238)} 1fr;
    }
  }
`

const AnytimeRateOptionTileDetails = styled.div`
  grid-area: details;
`

const AnytimeRateOptionTileInclusions = styled.div`
  grid-area: inclusions;
`

const AnytimeRateOptionTilePricing = styled.div`
  grid-column: 1 / -1;
  grid-row: auto;

  ${mediaQueryUp.tablet} {
    grid-column: auto;
    grid-row: 1 / -1;
  }
`

interface Props {
  property: App.Property.Property
  propertyRoom: App.Property.Room
  anytimeRoomRate: App.Property.AnytimeRoomRate | undefined
  duration: number
  showSelectDatesPrompt?: boolean
  layout?: 'offer-page' | 'room-card'
}

export function PropertyAnytimeRateOptionContent({
  property,
  propertyRoom,
  anytimeRoomRate,
  duration,
  showSelectDatesPrompt = false,
  layout = 'offer-page',
}: Props) {
  const intl = useIntl()
  const rateContent = useMemo(() => {
    const { promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions } = propertyRoom
    return {
      promotion: promotion && duration >= promotion.minimumLos ? promotion : undefined,
      inclusions: inclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      bonusInclusions: bonusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      luxPlusInclusions: luxPlusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
      bundleInclusions: bundleInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
    }
  }, [propertyRoom, duration])
  const { promotion, inclusions, bonusInclusions, luxPlusInclusions, bundleInclusions } = rateContent

  if (!promotion && !inclusions.length && !bonusInclusions.length && !luxPlusInclusions.length && !bundleInclusions.length) return null

  return <AnytimeRateOptionTile className={layout === 'room-card' ? 'layout-room-card' : undefined}>
    <AnytimeRateOptionTileDetails>
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={4}>
          {promotion && <LabelGroup>
            <PropertyPromotionLabel promotion={promotion} />
          </LabelGroup>}
          {showSelectDatesPrompt && <BodyText
            variant="large"
            weight="bold"
            colour="normal-contrast"
          >
            {intl.formatMessage({
              id: 'Q81wyQpQgKNK8UxtYyU6',
              defaultMessage: 'Select dates to unlock package options',
              description: 'Placeholder text when dates aren’t selected for property anytime rate option',
            })}
          </BodyText>}
        </VerticalSpacer>
        {promotion && <PropertyPromotionDetails promotion={promotion} />}
      </VerticalSpacer>
    </AnytimeRateOptionTileDetails>
    <AnytimeRateOptionTileInclusions>
      <PropertyRateInclusions
        property={property}
        inclusions={inclusions}
        bonusInclusions={bonusInclusions}
        luxPlusInclusions={luxPlusInclusions}
        bundleInclusions={bundleInclusions}
        maxHeight={208}
      />
    </AnytimeRateOptionTileInclusions>
    {(anytimeRoomRate?.duration === duration) && <AnytimeRateOptionTilePricing>
      <PropertyCheapestRatePriceDetails
        hotelOnlyRate={anytimeRoomRate}
        hotelWithFlightsRate={undefined}
        horizontalAlign="start"
      />
    </AnytimeRateOptionTilePricing>}
  </AnytimeRateOptionTile>
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  // Presence isn't permission: search tiles fetch anytime rates for any LPC/LPP property to find
  // dynamic Flash campaigns, so ask whether this property may surface them at all.
  anytimeRoomRate: getCanSurfacePropertyAnytimeRates(state, appState)
    ? appState.offer.propertyAnytimeRates[state.property!.id]?.anytimeRoomRates?.[ownProps.propertyRoom.id]
    : undefined,
  duration: state.duration,
  showSelectDatesPrompt: true,
  open: !!state.activeFilter,
}), PropertyAnytimeRateOptionContent, preventRenderWithOpenFilter)
